import React from 'react'
import { Template } from './TemplateSelector'
import MusicCreatorPro from './MusicCreatorPro'
import VisualArtistStudio from './VisualArtistStudio'
import CreatorHub from './CreatorHub'
import NFTCollection from './NFTCollection'
import DeFiProtocol from './DeFiProtocol'
import Web3Blog from './Web3Blog'
import DAOGovernance from './DAOGovernance'
import GamingMetaverse from './GamingMetaverse'
import CryptoInfluencer from './CryptoInfluencer'
import Web3Startup from './Web3Startup'
import NFTMarketplace from './NFTMarketplace'
import BlockchainDeveloper from './BlockchainDeveloper'

interface TemplateRendererProps {
  template: Template
  data?: any
}

export default function TemplateRenderer({ template, data }: TemplateRendererProps) {
  // Debug logging
  console.log('TemplateRenderer: Rendering template', template.id, template.name)
  
  // Default data for templates if none provided
  const defaultData = {
    artistName: 'Your Name',
    creatorName: 'Your Name', // For CreatorHub template
    blogName: 'Web3 Insights', // For Web3Blog template
    protocolName: 'YieldMax Protocol', // For DeFiProtocol template
    gameName: 'Metaverse Legends', // For GamingMetaverse template
    influencerName: 'Crypto King', // For CryptoInfluencer template
    startupName: 'BlockChain Solutions', // For Web3Startup template
    marketplaceName: 'NFT Paradise', // For NFTMarketplace template
    developerName: 'Blockchain Dev', // For BlockchainDeveloper template
    artistBio: 'Tell your story and showcase your work to the world.',
    creatorBio: 'Tell your story and showcase your work to the world.', // For CreatorHub template
    profileImage: '/api/placeholder/300/300',
    coverImage: '/api/placeholder/1920/1080',
    tracks: [
      {
        id: '1',
        title: 'Latest Release',
        duration: '3:45',
        plays: 1250,
        isPlaying: false
      },
      {
        id: '2',
        title: 'Fan Favorite',
        duration: '4:12',
        plays: 2100,
        isPlaying: false
      }
    ],
    tourDates: [
      {
        id: '1',
        venue: 'Concert Hall',
        location: 'New York, NY',
        date: 'March 15, 2024',
        ticketUrl: '#'
      }
    ],
    merchandise: [
      {
        id: '1',
        name: 'Official T-Shirt',
        price: '$25',
        image: '/api/placeholder/300/300',
        description: 'Premium quality merchandise'
      }
    ],
    socialLinks: {
      spotify: '#',
      appleMusic: '#',
      youtube: '#',
      instagram: '#',
      twitter: '#',
      tiktok: '#', // For CreatorHub template
      behance: '#', // For VisualArtistStudio template
      artstation: '#' // For VisualArtistStudio template
    },
    portfolio: [
      {
        id: '1',
        title: 'Featured Work',
        category: 'illustration',
        image: '/api/placeholder/400/400',
        description: 'Showcase your best work here',
        likes: 150,
        isLiked: false
      }
    ],
    commissionInfo: {
      title: 'Commission Information',
      description: 'I accept commissions for custom artwork and design projects.',
      pricing: 'Starting at $50',
      process: [
        'Contact me with your project details',
        'Discuss timeline and requirements',
        'Receive quote and timeline',
        'Begin work on your project'
      ],
      contactEmail: 'contact@example.com'
    },
    contactInfo: {
      email: 'contact@example.com',
      phone: '+1 (555) 123-4567',
      location: 'Your City, Country'
    },
    stats: {
      followers: 12500,
      totalViews: 250000,
      monthlyEarnings: 2500,
      contentCount: 45
    },
    content: [
      {
        id: '1',
        title: 'Latest Video',
        type: 'video' as const,
        thumbnail: '/api/placeholder/400/225',
        views: 5000,
        likes: 250,
        isPaid: false,
        publishedAt: '2 days ago'
      }
    ],
    subscriptionTiers: [
      {
        id: '1',
        name: 'Basic',
        price: 5,
        benefits: ['Access to basic content', 'Monthly newsletter'],
        subscriberCount: 150
      },
      {
        id: '2',
        name: 'Premium',
        price: 15,
        benefits: ['All content access', 'Exclusive content', 'Direct messaging'],
        subscriberCount: 75
      }
    ],
    blogPosts: [
      {
        id: '1',
        title: 'Welcome to My Blog',
        excerpt: 'This is where I share my thoughts, process, and behind-the-scenes content.',
        publishedAt: '1 week ago',
        readTime: '5 min read',
        category: 'Updates'
      }
    ],
    // Web3Blog specific data
    author: {
      name: 'Web3 Expert',
      avatar: '/api/placeholder/100/100',
      bio: 'Blockchain developer and Web3 enthusiast',
      socialLinks: {
        twitter: '#',
        github: '#',
        linkedin: '#'
      }
    },
    posts: [
      {
        id: '1',
        title: 'The Future of DeFi',
        excerpt: 'Exploring the latest trends in decentralized finance',
        content: 'Full article content here...',
        author: 'Web3 Expert',
        publishedAt: '2 days ago',
        readTime: '8 min read',
        category: 'DeFi',
        tags: ['DeFi', 'Blockchain', 'Finance'],
        views: 1250,
        likes: 45,
        comments: 12,
        featured: true,
        cryptoIntegration: {
          token: 'ETH',
          price: '$2,450',
          change: 5.2
        }
      }
    ],
    categories: [
      { id: '1', name: 'DeFi', count: 15, color: '#8B5CF6' },
      { id: '2', name: 'NFTs', count: 12, color: '#F59E0B' },
      { id: '3', name: 'DAO', count: 8, color: '#10B981' }
    ],
    newsletter: {
      title: 'Web3 Weekly',
      description: 'Stay updated with the latest Web3 news',
      subscribers: 2500
    },
    featuredPosts: [
      {
        id: '1',
        title: 'Top DeFi Protocols 2024',
        excerpt: 'The most promising DeFi projects to watch',
        image: '/api/placeholder/600/300',
        category: 'DeFi'
      }
    ],
    // NFT Collection default data
    collectionName: 'Digital Dreams',
    description: 'A unique collection of digital art pieces exploring the intersection of technology and creativity.',
    creator: 'DigitalArtist',
    contractAddress: '0x1234567890abcdef1234567890abcdef12345678',
    totalSupply: 10000,
    floorPrice: '0.5 ETH',
    volumeTraded: '1,250 ETH',
    owners: 3420,
    verified: true,
    nftSocialLinks: {
      twitter: 'https://twitter.com/digitaldreams',
      discord: 'https://discord.gg/digitaldreams',
      website: 'https://digitaldreams.art'
    },
    nfts: Array.from({ length: 8 }, (_, i) => ({
      id: (i + 1).toString(),
      name: `Digital Dream #${i + 1}`,
      image: '/api/placeholder/400/400',
      rarity: ['Legendary', 'Epic', 'Rare', 'Uncommon'][Math.floor(Math.random() * 4)],
      attributes: [
        { trait_type: 'Background', value: 'Cosmic', rarity_score: 0.05 },
        { trait_type: 'Eyes', value: 'Laser', rarity_score: 0.1 }
      ],
      lastSale: { price: '0.8', currency: 'ETH', date: '2 days ago' },
      currentPrice: { price: '1.2', currency: 'ETH' }
    })),
    nftStats: {
      totalVolume: '2,500 ETH',
      averagePrice: '0.75 ETH',
      highestSale: '15 ETH',
      listedCount: 1250
    },
    // DeFi Protocol default data
    defiProtocolName: 'YieldMax Protocol',
    totalValueLocked: '$125M',
    apy: '24.5%',
    apyChange: 2.3,
    totalUsers: 15420,
    totalPools: 12,
    defiTokenPrice: '$45.67',
    defiTokenPriceChange: 5.2,
    pools: [
      {
        id: '1',
        name: 'ETH/USDC Pool',
        tokens: ['ETH', 'USDC'],
        apy: '18.5%',
        tvl: '$45M',
        volume24h: '$2.3M',
        fees24h: '$23K',
        apyChange: 1.2
      },
      {
        id: '2',
        name: 'BTC/ETH Pool',
        tokens: ['BTC', 'ETH'],
        apy: '22.1%',
        tvl: '$38M',
        volume24h: '$1.8M',
        fees24h: '$18K',
        apyChange: -0.5
      }
    ],
    defiStats: {
      totalVolume: '$850M',
      totalFees: '$8.5M',
      activeUsers: 12500,
      transactions24h: 45600
    },
    // Gaming Metaverse specific data
    genre: 'MMORPG',
    platform: 'PC, Mobile',
    totalPlayers: 50000,
    onlinePlayers: 12500,
    gameTokenSymbol: 'GAME',
    gameTokenPrice: '$0.45',
    gameTokenPriceChange: 12.5,
    nftAssets: [
      {
        id: '1',
        name: 'Legendary Sword',
        type: 'weapon',
        rarity: 'legendary',
        price: '2.5 ETH',
        image: '/api/placeholder/300/300',
        stats: { power: 95, defense: 80, speed: 70 }
      }
    ],
    leaderboard: [
      {
        rank: 1,
        player: 'CryptoWarrior',
        score: 15420,
        level: 45,
        nftCount: 25,
        avatar: '/api/placeholder/50/50'
      }
    ],
    quests: [
      {
        id: '1',
        title: 'Dragon Slayer',
        description: 'Defeat the ancient dragon and claim the treasure',
        reward: '1000 GAME tokens',
        difficulty: 'hard',
        timeLimit: '24 hours',
        participants: 156,
        completed: false
      }
    ],
    features: [
      {
        title: 'High Yield Farming',
        description: 'Earn up to 25% APY with our optimized farming strategies.',
        icon: 'farming'
      },
      {
        title: 'Impermanent Loss Protection',
        description: 'Advanced algorithms to minimize impermanent loss for liquidity providers.',
        icon: 'protection'
      },
      {
        title: 'Auto-Compounding',
        description: 'Automatically reinvest your rewards for maximum compound growth.',
        icon: 'compounding'
      }
    ],
    security: {
      audits: 3,
      bugBounties: true,
      insurance: true,
      timelock: true
    },
    // Crypto Influencer specific data
    handle: '@cryptoking',
    bio: 'Crypto trader and blockchain educator with 500K+ followers',
    avatar: '/api/placeholder/200/200',
    followers: 500000,
    following: 2500,
    influencerVerified: true,
    influencerPortfolio: {
      totalValue: '$2.5M',
      dayChange: 5.2,
      dayChangeValue: '+$125K',
      topHoldings: [
        { symbol: 'BTC', name: 'Bitcoin', amount: '12.5', value: '$525K', change: 3.2, percentage: 21 },
        { symbol: 'ETH', name: 'Ethereum', amount: '500', value: '$1.25M', change: 4.8, percentage: 50 },
        { symbol: 'SOL', name: 'Solana', amount: '2500', value: '$375K', change: 8.5, percentage: 15 }
      ],
      performance: { day: 5.2, week: 12.8, month: 28.5, year: 185.3 }
    },
    trades: [
      {
        id: '1',
        type: 'buy',
        symbol: 'BTC',
        amount: '2.5',
        price: '$42,500',
        value: '$106,250',
        timestamp: '2 hours ago',
        profitLoss: '+$2,125'
      }
    ],
    analysis: [
      {
        id: '1',
        title: 'Bitcoin Breakout Analysis',
        excerpt: 'BTC showing strong bullish momentum with key resistance at $45K',
        publishedAt: '4 hours ago',
        views: 12500,
        likes: 450,
        category: 'Technical Analysis'
      }
    ],
    tradingSignals: [
      {
        id: '1',
        token: 'BTC',
        action: 'BUY',
        price: '$42,500',
        target: '$45,000',
        stopLoss: '$40,000',
        confidence: 85
      }
    ],
    // Web3 Startup specific data
    projectName: 'BlockChain Solutions',
    tagline: 'Revolutionary blockchain platform for decentralized applications',
    startupDescription: 'Revolutionary blockchain platform for decentralized applications',
    logo: '/api/placeholder/200/200',
    tokenSymbol: 'BCS',
    tokenPrice: '$0.25',
    tokenPriceChange: 12.5,
    marketCap: '$25M',
    startupTotalSupply: '100M',
    circulatingSupply: '40M',
    tokenomics: {
      distribution: [
        { category: 'Public Sale', percentage: 40, amount: '40M', vesting: 'Immediate' },
        { category: 'Team', percentage: 20, amount: '20M', vesting: '24 months' },
        { category: 'Advisors', percentage: 10, amount: '10M', vesting: '18 months' },
        { category: 'Reserve', percentage: 30, amount: '30M', vesting: '36 months' }
      ],
      utilities: [
        { title: 'Governance', description: 'Vote on protocol decisions', icon: 'vote' },
        { title: 'Staking', description: 'Earn rewards by staking tokens', icon: 'stake' },
        { title: 'Payments', description: 'Use tokens for platform fees', icon: 'payment' }
      ]
    },
    fundingRaised: '$5M',
    investors: ['Andreessen Horowitz', 'Coinbase Ventures', 'Binance Labs'],
    team: [
      {
        name: 'John Doe',
        role: 'CEO',
        bio: 'Former Google engineer with 10+ years blockchain experience',
        avatar: '/api/placeholder/100/100',
        socialLinks: { twitter: '#', linkedin: '#', github: '#' }
      }
    ],
    partnerships: [
      {
        name: 'Polygon',
        logo: '/api/placeholder/100/50',
        description: 'Layer 2 scaling partnership'
      }
    ],
    roadmap: [
      {
        phase: 'Phase 1',
        title: 'MVP Launch',
        description: 'Core platform functionality',
        status: 'completed',
        date: 'Q1 2024',
        features: ['Basic trading', 'Wallet integration']
      }
    ],
    // NFT Marketplace specific data
    marketplaceDescription: 'Premium NFT marketplace for digital collectibles',
    totalListings: 15000,
    totalVolume: '2.5M ETH',
    totalSales: 12500,
    marketplaceUsers: 8500,
    averagePrice: '0.75 ETH',
    featuredCollections: [
      {
        id: '1',
        name: 'CyberPunks',
        description: '10,000 unique cyberpunk characters',
        floorPrice: '0.8 ETH',
        volume: '500 ETH',
        items: 10000,
        owners: 4200,
        verified: true,
        image: '/api/placeholder/400/400'
      }
    ],
    trendingNFTs: [
      {
        id: '1',
        name: 'CyberPunk #1234',
        collection: 'CyberPunks',
        price: '1.2 ETH',
        lastSale: '0.9 ETH',
        image: '/api/placeholder/300/300',
        rarity: 'rare',
        creator: '0x123...456',
        verified: true
      }
    ],
    marketplaceCategories: [
      { id: '1', name: 'Art', count: 2500, icon: 'palette' },
      { id: '2', name: 'Gaming', count: 1800, icon: 'gamepad' },
      { id: '3', name: 'Music', count: 1200, icon: 'music' },
      { id: '4', name: 'Sports', count: 900, icon: 'trophy' }
    ],
    creators: [
      {
        id: '1',
        name: 'DigitalArtist',
        avatar: '/api/placeholder/100/100',
        verified: true,
        totalSales: '125 ETH',
        items: 50,
        followers: 2500,
        bio: 'Digital artist creating unique NFT collections'
      }
    ],
    marketplaceStats: {
      totalVolume: '2.5M ETH',
      totalSales: 12500,
      activeUsers: 8500,
      averagePrice: '0.75 ETH'
    },
    // Blockchain Developer specific data
    title: 'Senior Blockchain Developer',
    developerBio: 'Senior blockchain developer with 5+ years experience',
    location: 'San Francisco, CA',
    experience: '5+ years',
    developerVerified: true,
    skills: [
      { name: 'Solidity', level: 95, category: 'smart-contracts' },
      { name: 'Rust', level: 85, category: 'infrastructure' },
      { name: 'JavaScript', level: 90, category: 'infrastructure' },
      { name: 'Web3.js', level: 88, category: 'infrastructure' },
      { name: 'DeFi Protocols', level: 92, category: 'defi' },
      { name: 'Smart Contract Security', level: 90, category: 'security' }
    ],
    projects: [
      {
        id: '1',
        name: 'DeFi Protocol',
        description: 'Yield farming protocol with automated strategies',
        technology: 'Solidity, React, Node.js',
        category: 'defi',
        githubUrl: '#',
        liveUrl: '#',
        stars: 1250,
        forks: 156,
        language: 'Solidity',
        lastUpdated: '2 days ago',
        featured: true,
        status: 'completed'
      }
    ],
    contributions: [
      {
        id: '1',
        repository: 'ethereum/go-ethereum',
        type: 'pull-request',
        title: 'Optimize gas usage in transaction processing',
        description: 'Improved gas efficiency by 15%',
        date: '1 week ago',
        status: 'merged',
        impact: 'high'
      }
    ],
    audits: [
      {
        id: '1',
        protocol: 'DeFi Protocol V2',
        type: 'Smart Contract Audit',
        status: 'completed',
        date: '2024-01-15',
        findings: '3 low severity issues found and fixed',
        auditor: 'CertiK',
        reportUrl: '#'
      }
    ],
    certifications: [
      {
        name: 'Certified Ethereum Developer',
        issuer: 'Ethereum Foundation',
        date: '2023',
        credentialId: 'CED-2023-001'
      }
    ],
    // DAO Governance specific data
    daoName: 'Web3 DAO',
    daoTokenSymbol: 'DAO',
    daoTokenPrice: '$0.25',
    daoTokenPriceChange: 8.5,
    daoTotalSupply: '10M',
    treasury: {
      totalValue: '$2.5M',
      tokens: [
        { symbol: 'ETH', amount: '500', value: '$1.25M', percentage: 50 },
        { symbol: 'USDC', amount: '1M', value: '$1M', percentage: 40 },
        { symbol: 'DAO', amount: '100K', value: '$250K', percentage: 10 }
      ]
    },
    voting: {
      quorum: 25,
      votingPeriod: 7,
      minTokensToVote: 1000,
      totalVotingPower: '8.5M'
    },
    proposals: [
      {
        id: '1',
        title: 'Increase Treasury Allocation',
        description: 'Proposal to increase treasury allocation for development',
        proposer: '0x123...456',
        status: 'active',
        startDate: '2024-01-15',
        endDate: '2024-01-22',
        forVotes: 2500,
        againstVotes: 800,
        abstainVotes: 200,
        category: 'Treasury',
        type: 'treasury'
      }
    ],
    members: {
      total: 2500,
      active: 1800,
      newThisWeek: 45,
      topContributors: [
        {
          address: '0x123...456',
          name: 'DAO Leader',
          contributions: 25,
          votingPower: '50K'
        }
      ]
    }
  }

  const templateData = { ...defaultData, ...data }

  // Render the appropriate template based on the template ID
  try {
    switch (template.id) {
      case 'music-creator-pro':
        console.log('Rendering MusicCreatorPro')
        return <MusicCreatorPro data={templateData} />
      
      case 'visual-artist-studio':
        console.log('Rendering VisualArtistStudio')
        return <VisualArtistStudio data={templateData} />
      
      case 'creator-hub':
        console.log('Rendering CreatorHub')
        return <CreatorHub data={templateData} />
      
      case 'nft-collection':
        console.log('Rendering NFTCollection')
        return <NFTCollection data={templateData} />
      
      case 'defi-protocol':
        console.log('Rendering DeFiProtocol')
        return <DeFiProtocol data={templateData} />
      
      case 'web3-blog':
        console.log('Rendering Web3Blog')
        return <Web3Blog data={templateData} />
      
      case 'dao-governance':
        console.log('Rendering DAOGovernance')
        return <DAOGovernance data={templateData} />
      
      case 'gaming-metaverse':
        console.log('Rendering GamingMetaverse')
        return <GamingMetaverse data={templateData} />
      
      case 'crypto-influencer':
        console.log('Rendering CryptoInfluencer')
        return <CryptoInfluencer data={templateData} />
      
      case 'web3-startup':
        console.log('Rendering Web3Startup')
        return <Web3Startup data={templateData} />
      
      case 'nft-marketplace':
        console.log('Rendering NFTMarketplace')
        return <NFTMarketplace data={templateData} />
      
      case 'blockchain-developer':
        console.log('Rendering BlockchainDeveloper')
        return <BlockchainDeveloper data={templateData} />
    
      default:
        console.log('Template not found:', template.id)
        return (
          <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-purple-900 text-white flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                {template.name}
              </h1>
              <p className="text-purple-200 mb-8">{template.description}</p>
              <p className="text-red-400 mb-4">Template ID: {template.id}</p>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-8 max-w-md mx-auto">
                <p className="text-purple-200">
                  This template is coming soon! For now, you can use one of our other amazing templates.
                </p>
              </div>
            </div>
          </div>
        )
    }
  } catch (error) {
    console.error('Error rendering template:', template.id, error)
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-black to-red-900 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Template Error
          </h1>
          <p className="text-red-200 mb-8">There was an error loading the {template.name} template.</p>
          <div className="bg-red-800/50 backdrop-blur-sm border border-red-400/30 rounded-lg p-8 max-w-md mx-auto">
            <p className="text-red-200 text-sm">
              Error: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
            <p className="text-red-300 text-xs mt-2">Template ID: {template.id}</p>
          </div>
        </div>
      </div>
    )
  }
}
